/** Sakura manifest — 5×3 walking_wilds, per ADR-015. Theme assets per ADR-022/023. */
import type { GameSceneConfig } from '../types'

const BASE = '/assets/sakura/images'
const ABASE = '/assets/sakura/audio'
const NAME = 'sakura_neon_fortune_img'

const bg = (n: number, slug: string) => ({
  key: `sakura_${slug}`,
  path: `${BASE}/backgrounds/${NAME}_${String(n).padStart(3, '0')}_${slug}.webp`,
})
const sym = (n: number, slug: string) => ({
  key: `sakura_sym_${slug}`,
  path: `${BASE}/symbols/${NAME}_${String(n).padStart(3, '0')}_${slug}.webp`,
})
const popup = (n: number, slug: string) => ({
  key: `sakura_popup_${slug}`,
  path: `${BASE}/popups/${NAME}_${String(n).padStart(3, '0')}_${slug}.webp`,
})
const fx = (n: number, slug: string) => ({
  key: `sakura_fx_${slug}`,
  path: `${BASE}/effects/${NAME}_${String(n).padStart(3, '0')}_${slug}.webp`,
})
const btn = (n: number, slug: string) => ({
  key: `sakura_btn_${slug}`,
  path: `${BASE}/buttons/${NAME}_${String(n).padStart(3, '0')}_${slug}.webp`,
})
const brand = (n: number, slug: string) => ({
  key: `sakura_brand_${slug}`,
  path: `${BASE}/branding/${NAME}_${String(n).padStart(3, '0')}_${slug}.webp`,
})

const IMAGES = [
  // Branding
  brand(1, 'main_logo'),
  brand(2, 'compact_logo'),
  brand(6, 'brand_emblem'),
  // Backgrounds
  bg(13, 'main_game_background'),
  bg(14, 'alternate_main_background'),
  bg(15, 'free_spins_background'),
  bg(17, 'big_win_background'),
  bg(18, 'reel_frame_main'),
  bg(20, 'jackpot_header_main'),
  bg(22, 'bottom_control_bar'),
  bg(203, 'jackpot_header_hierarchy_v2'),
  bg(204, 'bottom_control_dock_v2'),
  bg(205, 'bottom_control_dock_hierarchy_v3'),
  bg(207, 'jackpot_header_balanced_v3'),
  // Symbols
  sym(31, 'sakura_blossom'),
  sym(32, 'lucky_cat'),
  sym(33, 'lantern'),
  sym(34, 'gold_coin'),
  sym(35, 'origami_crane'),
  sym(36, 'fox_mask'),
  sym(37, 'omamori_charm'),
  sym(38, 'bamboo_fan'),
  sym(39, 'tea_cup'),
  sym(40, 'temple_bell'),
  // Popups (ADR-023 — sakura 自己的 win/jackpot/fs 主题面板)
  popup(85, 'big_win_panel'),
  popup(86, 'mega_win_panel'),
  popup(87, 'super_win_panel'),
  popup(88, 'jackpot_win_panel'),
  popup(89, 'free_spins_start_panel'),
  popup(201, 'free_spins_start_screen_v2'),
  popup(202, 'free_spins_start_screen_v3'),
  popup(208, 'win_result_frame_v1'),
  // FX (粒子 + 庆祝光爆)
  fx(109, 'coin_burst'),
  fx(111, 'sakura_burst'),
  fx(112, 'sparkle_burst'),
  fx(114, 'symbol_highlight'),
  fx(115, 'win_flash'),
  fx(128, 'sakura_petal_sweep'),
  fx(129, 'celebration_burst'),
  // Buttons（spin / bet 控制 + 顶部 icon）
  btn(61, 'spin_button'),
  btn(62, 'spin_pressed_button'),
  btn(65, 'bet_plus_button'),
  btn(66, 'bet_minus_button'),
  btn(70, 'settings_button'),
  btn(71, 'info_button'),
  btn(72, 'back_button'),
  btn(73, 'close_button'),
  btn(206, 'spin_button_hierarchy_v3'),
] as const

const AUDIO = [
  // BGM 主题 + base game + win jingle stingers (J-JIN01..03)
  // 已删 dead assets（lobby/bonus/free_spins/daily_reward — 运行时从未播放，省 ~3M 加载）
  { key: 'sakura_bgm_main',           path: `${ABASE}/bgm/J-BGM01_main_theme.mp3` },
  { key: 'sakura_bgm_base',           path: `${ABASE}/bgm/J-BGM03_base_game_music.mp3` },
  { key: 'sakura_jin_small_win',      path: `${ABASE}/bgm/J-JIN01_small_win.mp3` },
  { key: 'sakura_jin_big_win',        path: `${ABASE}/bgm/J-JIN02_big_win.mp3` },
  { key: 'sakura_jin_jackpot_win',    path: `${ABASE}/bgm/J-JIN03_jackpot_win.mp3` },
  // SFX (J-SFX01..10)
  { key: 'sakura_sfx_spin_tap',       path: `${ABASE}/sfx/J-SFX01_spin_tap.mp3` },
  { key: 'sakura_sfx_reels_start',    path: `${ABASE}/sfx/J-SFX02_reel_spin_loop.mp3` },
  { key: 'sakura_sfx_reel_stop',      path: `${ABASE}/sfx/J-SFX03_reel_stop.mp3` },
  { key: 'sakura_sfx_wild_hit',       path: `${ABASE}/sfx/J-SFX04_wild_hit.mp3` },
  { key: 'sakura_sfx_scatter_hit',    path: `${ABASE}/sfx/J-SFX05_scatter_hit.mp3` },
  { key: 'sakura_sfx_free_spins_trg', path: `${ABASE}/sfx/J-SFX06_free_spins_trigger.mp3` },
  { key: 'sakura_sfx_coin_burst',     path: `${ABASE}/sfx/J-SFX07_coin_burst.mp3` },
  { key: 'sakura_sfx_coin_count',     path: `${ABASE}/sfx/J-SFX08_coin_count.mp3` },
  { key: 'sakura_sfx_popup_open',     path: `${ABASE}/sfx/J-SFX09_popup_open.mp3` },
] as const

export const SAKURA_SCENE_CONFIG: GameSceneConfig = {
  id: 'sakura',
  images: IMAGES,
  audio: AUDIO,
  bgMainKey: 'sakura_main_game_background',
  bgLoadingKey: 'sakura_alternate_main_background',
  // ADR-015: sakura 5×3 walking_wilds
  grid: { cols: 5, rows: 3, cellWidth: 172, cellHeight: 172 },
  symbolToAsset: {
    CHERRY:     'sakura_sym_sakura_blossom',
    LEMON:      'sakura_sym_lantern',
    WATERMELON: 'sakura_sym_origami_crane',
    STRAWBERRY: 'sakura_sym_bamboo_fan',
    BLUEBERRY:  'sakura_sym_tea_cup',
    RAINBOW:    'sakura_sym_lucky_cat',
    CROWN:      'sakura_sym_fox_mask',
    GOLDEN7:    'sakura_sym_temple_bell',
    WILD:       'sakura_sym_omamori_charm',
    SCATTER:    'sakura_sym_gold_coin',
    MULTIPLIER: 'sakura_sym_omamori_charm',
  },
  bgmMainKey:        'sakura_bgm_main',
  sfxSpinTapKey:     'sakura_sfx_spin_tap',
  sfxReelStopKey:    'sakura_sfx_reel_stop',
  // 用 Suno-生成的 J-JIN01 small / J-JIN02 big jingle 取代旧的通用 coin burst/count
  sfxSmallWinKey:    'sakura_jin_small_win',
  sfxBigWinCountKey: 'sakura_jin_big_win',
  themeAssets: {
    reelFrameKey:         'sakura_reel_frame_main',
    controlBarKey:        'sakura_bottom_control_dock_hierarchy_v3',
    spinButtonKey:        'sakura_btn_spin_button_hierarchy_v3',
    spinPressedKey:       'sakura_btn_spin_pressed_button',
    betPlusKey:           'sakura_btn_bet_plus_button',
    betMinusKey:          'sakura_btn_bet_minus_button',
    topIconBackKey:       'sakura_btn_back_button',
    topIconSettingsKey:   'sakura_btn_settings_button',
    topIconInfoKey:       'sakura_btn_info_button',
    topIconCloseKey:      'sakura_btn_close_button',
    winHighlightKey:      'sakura_fx_symbol_highlight',
    winFlashKey:          'sakura_fx_win_flash',
    sparkleParticleKey:   'sakura_fx_sparkle_burst',
    jackpotHeaderKey:     'sakura_jackpot_header_balanced_v3',
    bigWinPanelKey:       'sakura_popup_win_result_frame_v1',
    megaWinPanelKey:      'sakura_popup_win_result_frame_v1',
    superWinPanelKey:     'sakura_popup_win_result_frame_v1',
    jackpotWinPanelKey:   'sakura_popup_win_result_frame_v1',
    fsStartPanelKey:      'sakura_popup_free_spins_start_panel',
    fsStartScreenKey:     'sakura_popup_free_spins_start_screen_v3',
    fsBackdropKey:        'sakura_free_spins_background',
    bigWinBackdropKey:    'sakura_big_win_background',
    coinParticleKey:      'sakura_fx_coin_burst',
    driftParticleKey:     'sakura_fx_sakura_petal_sweep',
    celebrationBurstKey:  'sakura_fx_celebration_burst',
    sfxReelsStartKey:        'sakura_sfx_reels_start',
    sfxFreeSpinsTriggerKey:  'sakura_sfx_free_spins_trg',
    sfxFreeSpinStartKey:     'sakura_sfx_popup_open',
    // Jackpot stinger 用 J-JIN03 真正的 jackpot 音
    sfxBigWinStingerKey:     'sakura_jin_jackpot_win',
    sfxWildHitKey:           'sakura_sfx_wild_hit',
    // bgmFreeSpinsKey 已移除 —— 资源未被运行时播放且 crossfade 未实现。需要时一起加回
    palette: {
      primary:   0xec4899, // pink
      secondary: 0xa855f7, // purple
      deep:      0x2d0a3a, // deep purple
      gold:      0xfde047, // gold
      cream:     0xfff7c8, // cream / pale yellow
      txtStroke: '#5b1a8e',
    },
  },
}
